import { useEffect, useState } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation, useParams, useSearchParams } from "react-router-dom";
import { AppShell } from "./components/AppShell";
import { LoginPage } from "./pages/LoginPage";
import { KanbanPage } from "./pages/KanbanPage";
import { TriageInboxPage } from "./pages/TriageInboxPage";
import { TimesheetDetailPage } from "./pages/TimesheetDetailPage";
import { ExpenseDetailPage } from "./pages/ExpenseDetailPage";
import { ConflictPage } from "./pages/ConflictPage";
import { DocumentationPage } from "./pages/DocumentationPage";
import { AdminTestProtocolsPage } from "./pages/AdminTestProtocolsPage";
import { WerkstattGridAuditPage } from "./pages/WerkstattGridAuditPage";
import { WerkstattPage } from "./pages/WerkstattPage";
import { WerkstattZugriffPage } from "./pages/WerkstattZugriffPage";
import { WerkstattBestandPage } from "./pages/WerkstattBestandPage";
import { WerkstattPerformancePage } from "./pages/WerkstattPerformancePage";
import { SettingsPage } from "./pages/SettingsPage";
import { AuswertungPage } from "./pages/AuswertungPage";
import { api, type AuthUser } from "./lib/api";
import { ClientProvider } from "./lib/ClientContext";
import { ClientSettingsProvider } from "./lib/ClientSettingsContext";
import { ABRECHNUNG_PHASE_SLUGS } from "./lib/werkstattBranches";

/** Legacy redirect: /werkstatt/:phase (alte URLs) → /werkstatt/abrechnung/:phase
 *  wenn :phase ein bekannter Abrechnungs-Phase-Slug ist, sonst zur Default-Phase. */
function WerkstattLegacyRedirect() {
  const { phase } = useParams<{ phase: string }>();
  const [sp] = useSearchParams();
  const qs = sp.toString();
  const tail = qs ? `?${qs}` : "";
  const target = ABRECHNUNG_PHASE_SLUGS.includes(phase as any)
    ? `/werkstatt/abrechnung/${phase}${tail}`
    : `/werkstatt/abrechnung/upload${tail}`;
  return <Navigate to={target} replace />;
}

type AuthState = "loading" | "anonymous" | "authed";

export function App() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [state, setState] = useState<AuthState>("loading");
  const navigate = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    api.me()
      .then((u) => {
        setUser(u);
        setState("authed");
      })
      .catch(() => setState("anonymous"));
  }, []);

  if (state === "loading") return <div style={{ padding: 24 }}>Lade…</div>;

  // Inbox ist developer-only (2026-06-09, vor Kunden-Abnahme): die Triage-
  // Inbox ist nicht fertig — für user/admin ausgeblendet + Route umgeleitet,
  // damit nichts Halbfertiges in den Kunden-Flow gerät. Home = Kanban-Board.
  const isDeveloper = user?.role === "developer";
  const home = isDeveloper ? "/inbox" : "/board";

  const onLogin = (u: AuthUser) => {
    setUser(u);
    setState("authed");
    navigate(u.role === "developer" ? "/inbox" : "/board");
  };

  const onLogout = async () => {
    await api.logout();
    setUser(null);
    setState("anonymous");
    navigate("/login");
  };

  /** Developer-only route. Renders 403-Hinweis statt der Page wenn der User
   *  zwar eingeloggt aber kein 'developer' ist. (per Migration 033) */
  const protectDeveloper = (el: React.ReactElement): React.ReactElement => {
    if (state !== "authed" || !user) return el;
    if (user.role !== "developer") {
      return (
        <div style={{ padding: 24, maxWidth: 600, margin: "60px auto" }}>
          <h2>403 — Pipeline-Werkstatt</h2>
          <p className="muted">
            Die Pipeline-Werkstatt ist Developer-only (Glass-Box-Doku + Code-Pointer + Step-UUIDs).
            Dein Account hat Role <code>{user.role}</code>. Wenn du Zugriff brauchst,
            bitte einen Developer um ein Rollen-Upgrade.
          </p>
        </div>
      );
    }
    return el;
  };

  const protect = (el: React.ReactElement) =>
    state === "authed" && user ? (
      <ClientProvider>
        <ClientSettingsProvider>
          <AppShell user={user} onLogout={onLogout}>
            {el}
          </AppShell>
        </ClientSettingsProvider>
      </ClientProvider>
    ) : (
      <Navigate to="/login" state={{ from: loc.pathname }} replace />
    );

  // Modal-routes pattern: when a card is opened from board/inbox, the caller
  // pushes location.state.background = the original location. We then render
  // the BACKGROUND route (board/inbox stays mounted, scroll preserved) and
  // overlay the detail route on top. Direct links keep working — without a
  // background, the second <Routes> doesn't fire and the detail renders as
  // a full page, exactly like before.
  const bgLocation = (loc.state as { background?: Location } | null)?.background;

  return (
    <>
      <Routes location={bgLocation || loc}>
        <Route
          path="/login"
          element={state === "authed" ? <Navigate to={home} replace /> : <LoginPage onLogin={onLogin} />}
        />
        <Route path="/kanban" element={protect(<KanbanPage />)} />
        <Route path="/board" element={protect(<KanbanPage />)} />
        <Route path="/inbox" element={isDeveloper ? protect(<TriageInboxPage />) : <Navigate to="/board" replace />} />
        <Route path="/timesheets/:id" element={protect(<TimesheetDetailPage />)} />
        <Route path="/expenses/:id" element={protect(<ExpenseDetailPage />)} />
        <Route path="/conflicts/:kind/:id" element={protect(<ConflictPage />)} />
        <Route path="/auswertung" element={protect(<AuswertungPage />)} />
        <Route path="/documentation" element={protect(<DocumentationPage />)} />
        <Route path="/admin/test-protocols" element={protect(<AdminTestProtocolsPage />)} />
        <Route path="/admin/test-protocols/:id" element={protect(<AdminTestProtocolsPage />)} />
        <Route path="/labs" element={<Navigate to="/werkstatt/abrechnung/upload" replace />} />
        <Route path="/labs/ocr-test" element={<Navigate to="/werkstatt/abrechnung/ocr" replace />} />
        <Route path="/labs/abrechnung-test" element={<Navigate to="/werkstatt/abrechnung/berechnung" replace />} />
        <Route path="/werkstatt" element={<Navigate to="/werkstatt/abrechnung/upload" replace />} />
        <Route path="/werkstatt/abrechnung" element={<Navigate to="/werkstatt/abrechnung/upload" replace />} />
        <Route path="/werkstatt/abrechnung/:phase" element={protect(protectDeveloper(<WerkstattPage />))} />
        <Route path="/werkstatt/zugriff" element={<Navigate to="/werkstatt/zugriff/matrix" replace />} />
        <Route path="/werkstatt/zugriff/:section" element={protect(protectDeveloper(<WerkstattZugriffPage />))} />
        <Route path="/werkstatt/bestand" element={protect(protectDeveloper(<WerkstattBestandPage />))} />
        <Route path="/werkstatt/bestand/:section" element={protect(protectDeveloper(<WerkstattBestandPage />))} />
        <Route path="/werkstatt/performance" element={<Navigate to="/werkstatt/performance/karten-open" replace />} />
        <Route path="/werkstatt/performance/:section" element={protect(protectDeveloper(<WerkstattPerformancePage />))} />
        <Route path="/werkstatt/grid-audit" element={<Navigate to="/werkstatt/grid-audit/card" replace />} />
        <Route path="/werkstatt/grid-audit/:section" element={protect(protectDeveloper(<WerkstattGridAuditPage />))} />
        {/* Backwards-compat: alte /werkstatt/:phase URLs umleiten — muss NACH den literal segments stehen */}
        <Route path="/werkstatt/:phase" element={<WerkstattLegacyRedirect />} />
        <Route path="/settings" element={protect(<SettingsPage currentUser={user!} />)} />
        <Route path="/settings/:tab" element={protect(<SettingsPage currentUser={user!} />)} />
        <Route path="*" element={<Navigate to={state === "authed" ? home : "/login"} replace />} />
      </Routes>
      {bgLocation && (
        <Routes>
          <Route path="/timesheets/:id" element={protect(<TimesheetDetailPage modal />)} />
          <Route path="/expenses/:id" element={protect(<ExpenseDetailPage modal />)} />
          <Route path="/conflicts/:kind/:id" element={protect(<ConflictPage />)} />
          <Route path="/werkstatt/abrechnung/:phase" element={protect(protectDeveloper(<WerkstattPage modal />))} />
          <Route path="/werkstatt/zugriff/:section" element={protect(protectDeveloper(<WerkstattZugriffPage modal />))} />
          <Route path="/werkstatt/bestand" element={protect(protectDeveloper(<WerkstattBestandPage modal />))} />
          <Route path="/werkstatt/bestand/:section" element={protect(protectDeveloper(<WerkstattBestandPage modal />))} />
          <Route path="/werkstatt/performance/:section" element={protect(protectDeveloper(<WerkstattPerformancePage modal />))} />
          <Route path="/werkstatt/:phase" element={<WerkstattLegacyRedirect />} />
        </Routes>
      )}
    </>
  );
}
